import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import api, { getApiErrorMessage } from "@/lib/api";
import { ADDONS, fmtMoney, BASE_SETUP_CENTS, BASE_MONTHLY_CENTS } from "@/lib/pricing";
import { getPublicBaseUrl } from "@/lib/publicBase";
import {
  Package as PackageIcon,
  Send,
  Copy as CopyIcon,
  Check,
  Loader2,
  ExternalLink,
  CircleDollarSign,
} from "lucide-react";

const STATUS_META = {
  draft: { label: "Draft", color: "#A0B0AB" },
  proposal_sent: { label: "Proposal sent", color: "#60A5FA" },
  signed: { label: "Signed — awaiting payment", color: "#F4B860" },
  paid: { label: "Paid · Active", color: "#4ADE80" },
  canceled: { label: "Canceled", color: "#F87171" },
};

export default function OrderBuilder({ placeId, onOrderChanged }) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [selected, setSelected] = useState({}); // { key: {enabled, quantity, use_bundle} }
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/leads/${encodeURIComponent(placeId)}/order`);
      if (data) {
        setOrder(data);
        const sel = {};
        (data.addons || []).forEach((a) => {
          sel[a.key] = { enabled: true, quantity: a.quantity, use_bundle: !!a.use_bundle };
        });
        setSelected(sel);
      } else {
        setOrder(null);
        setSelected({});
      }
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [placeId]);

  useEffect(() => {
    load();
  }, [load]);

  const preview = useMemo(() => {
    // Local preview totals (backend recomputes on save)
    let oneTime = BASE_SETUP_CENTS;
    let monthly = BASE_MONTHLY_CENTS;
    ADDONS.forEach((spec) => {
      const s = selected[spec.key];
      if (!s?.enabled) return;
      const qty = Math.max(1, s.quantity || 1);
      if (s.use_bundle && spec.bundle) {
        const bundles = Math.floor(qty / spec.bundle.qty);
        const rem = qty % spec.bundle.qty;
        oneTime += bundles * spec.bundle.one_time + rem * spec.one_time;
      } else {
        oneTime += qty * spec.one_time;
      }
      monthly += qty * spec.monthly;
    });
    return { oneTime, monthly };
  }, [selected]);

  const locked = order?.status === "signed" || order?.status === "paid";

  const toggle = (key, enabled) => {
    setSelected((s) => ({
      ...s,
      [key]: { enabled, quantity: s[key]?.quantity || 1, use_bundle: !!s[key]?.use_bundle },
    }));
  };

  const setQty = (key, qty) => {
    setSelected((s) => ({
      ...s,
      [key]: { enabled: true, quantity: Math.max(1, qty), use_bundle: !!s[key]?.use_bundle },
    }));
  };

  const setBundle = (key, use_bundle) => {
    setSelected((s) => ({
      ...s,
      [key]: { enabled: true, quantity: s[key]?.quantity || 1, use_bundle },
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const addons = ADDONS.filter((spec) => selected[spec.key]?.enabled).map((spec) => ({
        key: spec.key,
        quantity: Math.max(1, selected[spec.key].quantity || 1),
        use_bundle: !!selected[spec.key].use_bundle,
      }));
      const { data } = await api.put(`/leads/${encodeURIComponent(placeId)}/order`, { addons });
      setOrder(data);
      toast.success("Order saved");
      onOrderChanged && onOrderChanged(data);
    } catch (e) {
      // Show the actual backend message ("Cannot modify signed/paid order", validation msgs, DB errors, etc.)
      // Fall back only if literally nothing usable came back.
      const msg = getApiErrorMessage(e);
      toast.error(msg && msg !== "Request failed" ? msg : "Save failed — check your connection and try again.");
      // If the server said the order is already signed/paid (409), refetch so the UI shows the locked banner.
      const status = e?.response?.status;
      if (status === 409) {
        await load();
      }
    } finally {
      setSaving(false);
    }
  };

  const sendProposal = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/leads/${encodeURIComponent(placeId)}/order/send-proposal`);
      await load();
      toast.success("Proposal link generated");
      onOrderChanged && onOrderChanged({ ...order, status: "proposal_sent", proposal_token: data.proposal_token });
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setSending(false);
    }
  };

  const proposalUrl = order?.proposal_token
    ? `${getPublicBaseUrl()}/p/${order.proposal_token}`
    : null;

  const copyProposal = async () => {
    if (!proposalUrl) return;
    await navigator.clipboard.writeText(proposalUrl);
    setCopied(true);
    toast.success("Proposal link copied");
    setTimeout(() => setCopied(false), 1500);
  };

  if (loading) {
    return (
      <div className="p-6 space-y-3" data-testid="order-builder-loading">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-14 border border-line rounded-sm animate-pulse bg-bg-hover" />
        ))}
      </div>
    );
  }

  const statusMeta = order ? STATUS_META[order.status] : null;

  return (
    <div className="p-6 space-y-5" data-testid="order-builder">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-base font-medium text-ink flex items-center gap-2">
            <PackageIcon size={14} className="text-cyan" />
            Order builder
          </h3>
          <p className="text-xs text-ink-muted mt-1">
            Base package is always Setup + Monthly. Toggle add-ons; totals compute automatically.
          </p>
        </div>
        {statusMeta && (
          <span
            className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-sm border"
            style={{ color: statusMeta.color, borderColor: statusMeta.color + "55", backgroundColor: statusMeta.color + "12" }}
            data-testid="order-status-pill"
          >
            {statusMeta.label}
          </span>
        )}
      </div>

      {/* Locked banner — big and obvious so reps immediately understand why edits are blocked */}
      {locked && (
        <div
          className="border border-cyan/40 bg-cyan/5 rounded-sm p-4"
          data-testid="order-locked-banner"
        >
          <div className="text-sm font-medium text-ink flex items-center gap-2">
            <span className="text-cyan">●</span>
            {order?.status === "paid" ? "This deal is closed and paid" : "This proposal has been signed"}
          </div>
          <div className="text-[13px] text-ink-secondary mt-1.5 leading-relaxed max-w-2xl">
            The order is locked and can&rsquo;t be edited. If the customer needs a change:
            add or upgrade an add-on{" "}
            <span className="font-mono text-ink">directly in Stripe</span> (or refund + create a
            new order). To sell a NEW deal, pick a different lead.
          </div>
        </div>
      )}

      {/* Base package */}
      <div className="border border-line rounded-sm bg-bg-surface" data-testid="order-base-package">
        <div className="px-4 py-3 border-b border-line text-[11px] font-mono uppercase tracking-widest text-ink-muted">
          Base package (always included)
        </div>
        <div className="divide-y divide-line">
          <BaseRow label="Setup — build + full customization round" oneTime={BASE_SETUP_CENTS} monthly={0} />
          <BaseRow label="Monthly — security, maintenance, hosting, 2-3 edits/mo, GBP + reviews mgmt" oneTime={0} monthly={BASE_MONTHLY_CENTS} />
        </div>
      </div>

      {/* Add-ons */}
      <div className="border border-line rounded-sm bg-bg-surface" data-testid="order-addons">
        <div className="px-4 py-3 border-b border-line text-[11px] font-mono uppercase tracking-widest text-ink-muted">
          Add-ons
        </div>
        <div className="divide-y divide-line">
          {ADDONS.map((spec) => {
            const s = selected[spec.key] || { enabled: false, quantity: 1, use_bundle: false };
            return (
              <AddonRow
                key={spec.key}
                spec={spec}
                state={s}
                disabled={locked}
                onToggle={(v) => toggle(spec.key, v)}
                onQty={(q) => setQty(spec.key, q)}
                onBundle={(b) => setBundle(spec.key, b)}
              />
            );
          })}
        </div>
      </div>

      {/* Totals */}
      <div className="border border-cyan/40 rounded-sm bg-cyan-muted p-4 grid grid-cols-2 gap-4" data-testid="order-totals">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">
            One-time (due on signing)
          </div>
          <div className="text-2xl font-semibold text-ink tabular-nums mt-1" data-testid="order-one-time-total">
            {fmtMoney(preview.oneTime)}
          </div>
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">
            Monthly (recurring)
          </div>
          <div className="text-2xl font-semibold text-ink tabular-nums mt-1" data-testid="order-monthly-total">
            {fmtMoney(preview.monthly)}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={save}
          disabled={saving || locked}
          data-testid="order-save-btn"
          className="flex items-center gap-2 bg-cyan text-bg font-medium py-2 px-4 rounded-sm hover:bg-cyan-hover transition-colors disabled:opacity-60 active:scale-[0.98]"
        >
          <CircleDollarSign size={14} />
          {saving ? "Saving…" : order ? "Update order" : "Save order"}
        </button>
        <button
          onClick={sendProposal}
          disabled={sending || !order || locked}
          data-testid="order-send-proposal-btn"
          className="flex items-center gap-2 border border-line text-ink hover:bg-bg-hover py-2 px-4 rounded-sm transition-colors disabled:opacity-40"
        >
          <Send size={14} />
          {sending ? "Generating…" : order?.status === "proposal_sent" ? "Regenerate proposal link" : "Send proposal"}
        </button>
        {locked && (
          <span className="text-[11px] text-ink-muted italic">
            Order is signed — edits are locked.
          </span>
        )}
      </div>

      {proposalUrl && (
        <div
          className="border border-line rounded-sm bg-bg p-3 flex items-center gap-3"
          data-testid="proposal-link-box"
        >
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">
              Proposal link · share with the prospect
            </div>
            <div className="text-sm text-ink font-mono truncate mt-0.5" data-testid="proposal-url">
              {proposalUrl}
            </div>
          </div>
          <button
            onClick={copyProposal}
            data-testid="copy-proposal-link-btn"
            className="flex items-center gap-1 text-xs border border-line text-ink hover:bg-bg-hover px-2 py-1 rounded-sm transition-colors"
          >
            {copied ? <Check size={12} className="text-cyan" /> : <CopyIcon size={12} />}
            {copied ? "Copied" : "Copy"}
          </button>
          <a
            href={proposalUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="open-proposal-link-btn"
            className="flex items-center gap-1 text-xs border border-line text-ink hover:bg-bg-hover px-2 py-1 rounded-sm transition-colors"
          >
            <ExternalLink size={12} />
            Open
          </a>
        </div>
      )}

      {order?.status === "paid" && (
        <div
          className="border rounded-sm p-4"
          style={{
            backgroundColor: "rgba(74, 222, 128, 0.06)",
            borderColor: "rgba(74, 222, 128, 0.35)",
          }}
          data-testid="order-paid-banner"
        >
          <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "#4ADE80" }}>
            Paid
          </div>
          <div className="text-sm text-ink mt-1">
            Setup collected, subscription started. Lead moved to Closed-Won.
          </div>
        </div>
      )}
    </div>
  );
}

function BaseRow({ label, oneTime, monthly }) {
  return (
    <div className="px-4 py-3 flex items-center gap-4">
      <div className="flex-1 min-w-0">
        <div className="text-sm text-ink">{label}</div>
      </div>
      <div className="text-right shrink-0">
        {oneTime > 0 && (
          <div className="text-sm text-ink tabular-nums font-mono">{fmtMoney(oneTime)}</div>
        )}
        {monthly > 0 && (
          <div className="text-xs text-ink-secondary tabular-nums font-mono">
            {fmtMoney(monthly)}/mo
          </div>
        )}
      </div>
    </div>
  );
}

function AddonRow({ spec, state, onToggle, onQty, onBundle, disabled }) {
  const showBundle = !!spec.bundle;
  return (
    <div
      className={`px-4 py-3 flex items-center gap-4 ${state.enabled ? "bg-bg-hover" : ""}`}
      data-testid={`addon-row-${spec.key}`}
    >
      <label className="flex items-center gap-3 min-w-0 flex-1 cursor-pointer">
        <input
          type="checkbox"
          checked={!!state.enabled}
          onChange={(e) => onToggle(e.target.checked)}
          disabled={disabled}
          data-testid={`addon-toggle-${spec.key}`}
          className="h-4 w-4 shrink-0 accent-cyan"
        />
        <div className="min-w-0">
          <div className="text-sm text-ink flex items-center gap-2">
            {spec.name}
            {spec.lead && (
              <span
                className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border"
                style={{ color: "#F4B860", borderColor: "rgba(244, 184, 96, 0.35)" }}
              >
                Lead upsell
              </span>
            )}
          </div>
          <div className="text-[11px] text-ink-muted mt-0.5">{spec.note}</div>
        </div>
      </label>

      {state.enabled && (
        <div className="flex items-center gap-2 shrink-0">
          <div className="flex items-center gap-1 border border-line rounded-sm">
            <button
              onClick={() => onQty(Math.max(1, (state.quantity || 1) - 1))}
              disabled={disabled}
              data-testid={`addon-qty-dec-${spec.key}`}
              className="px-2 py-1 text-sm text-ink-secondary hover:text-ink hover:bg-bg-hover transition-colors disabled:opacity-40"
              type="button"
            >
              −
            </button>
            <div
              className="px-2 py-1 text-sm text-ink tabular-nums font-mono w-8 text-center"
              data-testid={`addon-qty-${spec.key}`}
            >
              {state.quantity || 1}
            </div>
            <button
              onClick={() => onQty((state.quantity || 1) + 1)}
              disabled={disabled}
              data-testid={`addon-qty-inc-${spec.key}`}
              className="px-2 py-1 text-sm text-ink-secondary hover:text-ink hover:bg-bg-hover transition-colors disabled:opacity-40"
              type="button"
            >
              +
            </button>
          </div>
          {showBundle && (state.quantity || 1) >= spec.bundle.qty && (
            <label className="flex items-center gap-1 text-[11px] text-ink-secondary cursor-pointer">
              <input
                type="checkbox"
                checked={!!state.use_bundle}
                onChange={(e) => onBundle(e.target.checked)}
                disabled={disabled}
                data-testid={`addon-bundle-${spec.key}`}
                className="h-3 w-3 accent-cyan"
              />
              Bundle 3-for-$599
            </label>
          )}
        </div>
      )}

      <div className="text-right shrink-0 min-w-[110px]">
        {spec.one_time > 0 && (
          <div className="text-sm text-ink tabular-nums font-mono">{fmtMoney(spec.one_time)}</div>
        )}
        {spec.monthly > 0 && (
          <div className="text-xs text-ink-secondary tabular-nums font-mono">
            {fmtMoney(spec.monthly)}/mo
          </div>
        )}
      </div>
    </div>
  );
}
